import { InfoOutlined, Search } from '@mui/icons-material'
import { Box, InputAdornment, List, ListItemButton, TextField, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CenteredSpinner } from 'src/components/Spinner'
import { IMoveReportTarget } from 'src/generated-types/form-schema'
import { MUIThemeColors } from 'src/mui-theme'
import { GetRGBA, getThemeColor } from 'src/utils'

interface IMoveReportTargetListProps {
  searchText: string
  onSearchTextChange: (value: string) => void
  searchPlaceholder: string
  targets: IMoveReportTarget[]
  isLoading: boolean
  emptyText: string
  selected?: IMoveReportTarget
  onSelect: (target: IMoveReportTarget) => void
  loadedCount?: number
  totalCount?: number
}

export const MoveReportTargetList: React.FC<IMoveReportTargetListProps> = ({
  searchText,
  onSearchTextChange,
  searchPlaceholder,
  targets,
  isLoading,
  emptyText,
  selected,
  onSelect,
  loadedCount = 0,
  totalCount = 0,
}) => {
  const { t } = useTranslation(['form-schema-report'])

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <TextField
        size="small"
        fullWidth
        autoFocus
        value={searchText}
        placeholder={searchPlaceholder}
        onChange={e => onSearchTextChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: '18px' }} />
            </InputAdornment>
          ),
        }}
      />
      {totalCount > loadedCount && (
        <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
          <InfoOutlined sx={{ fontSize: '16px' }} />
          <Typography variant="caption">
            {t('showing-loaded-of-total', { ns: 'form-schema-report', loaded: loadedCount, total: totalCount })}
          </Typography>
        </Box>
      )}
      {isLoading ? (
        <CenteredSpinner height={280} />
      ) : targets.length === 0 ? (
        <Box display="flex" alignItems="center" justifyContent="center" height="280px">
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        </Box>
      ) : (
        <StyledList dense disablePadding>
          {targets.map(target => (
            <StyledListItemButton
              key={target.id}
              selected={target.id === selected?.id}
              onClick={() => onSelect(target)}
            >
              <Box display="flex" flexDirection="column" width="100%">
                <Typography variant="body2" fontWeight={500}>
                  {target.label}
                </Typography>
                {target.secondaryLabel && (
                  <Typography variant="caption" color="text.secondary">
                    {target.secondaryLabel}
                  </Typography>
                )}
              </Box>
            </StyledListItemButton>
          ))}
        </StyledList>
      )}
    </Box>
  )
}

const StyledList = styled(List)(({ theme }) => ({
  height: '280px',
  overflowY: 'auto',
  border: `1px solid ${GetRGBA(getThemeColor(theme, MUIThemeColors.black), 0.12)}`,
  borderRadius: '4px',
}))

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: GetRGBA(getThemeColor(theme, MUIThemeColors.primaryMain), 0.12),
  },
  '&.Mui-selected:hover': {
    backgroundColor: GetRGBA(getThemeColor(theme, MUIThemeColors.primaryMain), 0.18),
  },
}))
